import { WalletName } from '@solana/wallet-adapter-base'
import { Wallet, useWallet } from '@solana/wallet-adapter-react'
import { useMemo } from 'react'
import { canLink, canSignTransaction, isReachable } from '../capabilities'

export type WalletRequirement =
    /** Linking an account: either proof works, the signer picks based on the adapter. */
    | 'link'
    /** Paying: the wallet has to be able to sign a transaction we build. */
    | 'transaction'

type Options = {
    require?: WalletRequirement
    /** Wallets to hide by name, for reasons capability flags cannot express. */
    exclude?: string[]
}

/**
 * The single source of truth for "which wallets do we show".
 *
 * `useWallet().wallets` is longer than it looks. Beyond the adapters registered in
 * `WalletContext`, `@solana/wallet-adapter-react` mixes in every wallet that announced itself
 * through the Wallet Standard -- Backpack, Glow, Nightly, OKX, MetaMask once it has a Solana
 * account, Brave Wallet when Brave is not delegating Solana to extensions -- plus Mobile Wallet
 * Adapter on Android. That list is different for every visitor, which is exactly why hardcoded
 * assumptions about it kept producing "works for me" bug reports.
 */
export const useAvailableWallets = ({ require = 'link', exclude = [] }: Options = {}) => {
    const { wallets } = useWallet()

    return useMemo(() => {
        // No special case for Ledger and WebHID: its adapter already resolves its own
        // `readyState` to `Unsupported` when `navigator.hid` is absent, so `isReachable` drops it
        // in Firefox and Safari without help.
        const available = wallets.filter(({ adapter }) => {
            if (exclude.includes(adapter.name)) return false
            if (!isReachable(adapter)) return false

            return require === 'transaction' ? canSignTransaction(adapter) : canLink(adapter)
        })

        return { available }
    }, [wallets, require, exclude.join(',')])
}

/**
 * Sort helper for pickers that want installed extensions first -- a player with Phantom installed
 * should not have to scroll past six hosted wallets to find it.
 */
export const byInstalledFirst = (a: Wallet, b: Wallet) =>
    Number(b.adapter.readyState === 'Installed') - Number(a.adapter.readyState === 'Installed')

export const walletName = (name: string) => name as WalletName
